import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { API } from "../../../shared/api/Api";
import { showToast } from "../../../utils/utils";
import MaterialTable from "../../../components/Tables/MaterialTable";
import { selectUserInformations } from "../../../features/authSlice";
import { FiArrowLeft } from "react-icons/fi";

const DEBOUNCE_DELAY = 700;

const CourseMaterialPage = () => {
  const [reFetchData, setReFetchData] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loader, setLoader] = useState(true);

  const params = useParams();
  const user = useSelector(selectUserInformations);
  const location = useLocation();

  const fetchMaterials = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API.Materials.app_courses_materials_allMaterialsForCourse}/${params.id}`,
        {
          params: { page: currentPage, search: debouncedSearch },
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );

      if (data.status === 200 && data.errorCode === 0) {
        setReFetchData(false);
        setMaterials(data.data.items);
        setCurrentPage(data.data.current_page);
        setTotalPages(data.data.last_page);
      } else {
        showToast("Error", "Failed to fetch materials", "error"); // Corrected entity name
      }
    } catch (error) {
      showToast(
        "Error",
        error.response?.data?.message || "An error occurred",
        "error"
      );
    }
  }, [currentPage, debouncedSearch, user.token, params.id]); // Added params.id

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1); // Reset to the first page on search change
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      setLoader(true); // Show loader during fetching
      try {
        setMaterials([]);
        await fetchMaterials();
      } catch (error) {
        showToast(
          "Error",
          error.message || "An error occurred", // Use error.message
          "error"
        );
      } finally {
        setLoader(false);
      }
    };
    fetchData();
  }, [fetchMaterials, reFetchData]); 

  const courseName = location.state?.course?.title || "Course";

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      {/* Header with Back Navigation */}
      <div className="mb-4 flex items-center gap-4">
        <Link
          to={`/dashboard/courses/${params.id}`}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
        >
          <FiArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Materials for {courseName}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            View and manage course materials
          </p>
        </div>
      </div>

      <MaterialTable
        materials={materials}
        currentPage={currentPage}
        totalPages={totalPages}
        onSearchChange={setSearch}
        onPageChange={setCurrentPage}
        loader={loader}
        setReFetchData={setReFetchData}
        courseStudents={true}
        courseData={location.state}
      />
    </div>
  );
};

export default CourseMaterialPage;
